import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  sections: [],
  templates: [],
  loading: true,
  isSuccess: false,
  errors: []
};

export const fetchAreaSections = createAsyncThunk(
  'areaSections/fetchAll',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/company/area-sections');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchAreaSectionTemplates = createAsyncThunk(
  'areaSections/fetchTemplates',
  async (businessAreaId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(
        `/businessarea/${businessAreaId}/area-section-templates`
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const uploadAreaSectionImage = createAsyncThunk(
  'areaSections/uploadImage',
  async ({ sectionKey, formData }, { rejectWithValue, dispatch }) => {
    try {
      await api.post(`/company/area-section/${sectionKey}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const refresh = await api.get('/company/area-sections');
      return refresh.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const updateAreaSectionItem = createAsyncThunk(
  'areaSections/updateItem',
  async ({ sectionKey, itemId, formData }, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/company/area-section/${sectionKey}/item/${itemId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const refresh = await api.get('/company/area-sections');
      return refresh.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const deleteAreaSectionItem = createAsyncThunk(
  'areaSections/deleteItem',
  async ({ sectionKey, itemId }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/company/area-section/${sectionKey}/item/${itemId}`);
      const refresh = await api.get('/company/area-sections');
      return refresh.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

const areaSectionsSlice = createSlice({
  name: 'areaSections',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAreaSections.pending, (state) => { state.loading = true; })
      .addCase(fetchAreaSections.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = true;
        state.sections = payload || [];
      })
      .addCase(fetchAreaSections.rejected, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = false;
        if (payload?.statusCode === 400) state.errors = payload?.message;
      })

      .addCase(fetchAreaSectionTemplates.pending, (state) => { state.loading = true; })
      .addCase(fetchAreaSectionTemplates.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.templates = [...(payload || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      })
      .addCase(fetchAreaSectionTemplates.rejected, (state) => { state.loading = false; })

      .addCase(uploadAreaSectionImage.pending, (state) => { state.loading = true; })
      .addCase(uploadAreaSectionImage.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = true;
        state.sections = payload || [];
      })
      .addCase(uploadAreaSectionImage.rejected, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = false;
        if (payload?.statusCode === 400) state.errors = payload?.message;
      })

      .addCase(updateAreaSectionItem.pending, (state) => { state.loading = true; })
      .addCase(updateAreaSectionItem.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = true;
        state.sections = payload || [];
      })
      .addCase(updateAreaSectionItem.rejected, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = false;
        if (payload?.statusCode === 400) state.errors = payload?.message;
      })

      .addCase(deleteAreaSectionItem.pending, (state) => { state.loading = true; })
      .addCase(deleteAreaSectionItem.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = true;
        state.sections = payload || [];
      })
      .addCase(deleteAreaSectionItem.rejected, (state, { payload }) => {
        state.loading = false;
        state.isSuccess = false;
        if (payload?.statusCode === 400) state.errors = payload?.message;
      });
  }
});

export default areaSectionsSlice.reducer;
